import { AfterViewChecked, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CaseService } from '@secca/core/services/case.service';
import { DataService } from '@secca/core/services/data.service';
import { IncidentService } from '@secca/core/services/incident.service';
import { CaseLockHelperService } from '@secca/core/services/case-lock-helper.service';
import { AtlasFraudDetectionService } from '@secca/core/services/atlas/atlas-fraud-detection.service';
import { Case } from '@secca/shared/models/case';
import { KeycloakService } from 'keycloak-angular';
import { firstValueFrom } from 'rxjs';
import { filter, take, timeout } from 'rxjs/operators';
import { NavigationEnd, Router } from '@angular/router';
import { MatDialogRef } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { DictionaryService } from '@secca/core/services/dictionary.service';
import { AtlasChatbotFirstContactTemplateDialogComponent } from '@secca/shared/components/atlas-chatbot/first-contact-template-dialog/atlas-chatbot-first-contact-template-dialog.component';
import {
  AtlasCaseValidationDialogComponent,
  AtlasCaseValidationDialogData
} from '@secca/shared/components/atlas-chatbot/case-validation-dialog/atlas-case-validation-dialog.component';
import {
  AtlasFraudDetectionDialogComponent,
  AtlasFraudDetectionDialogData
} from '@secca/shared/components/atlas-chatbot/fraud-detection-dialog/atlas-fraud-detection-dialog.component';
import { AtlasChatbotInfoDialogComponent } from '@secca/shared/components/atlas-chatbot/info-dialog/atlas-chatbot-info-dialog.component';
import { DocumentTranslationsDialogComponent } from '@secca/shared/components/atlas-chatbot/document-translations-dialog/document-translations-dialog.component';
import { DialogHelperUtilService } from '@secca/core/services/dialog-helper-util.service';
import { AtlasChatService } from '@secca/core/services/atlas/atlas-chat.service';
import {
  AtlasChatCaseContextData,
  AtlasChatPersistedSession,
  AtlasChatUsageStatusResponse
} from '@secca/shared/models/atlas/atlas-chat.model';
import { AtlasChatbotContextInputsDialogComponent } from '@secca/shared/components/atlas-chatbot/context-inputs-dialog/atlas-chatbot-context-inputs-dialog.component';
import { AtlasChatbotChatHistoryDialogComponent } from '@secca/shared/components/atlas-chatbot/chat-history-dialog/atlas-chatbot-chat-history-dialog.component';
import {
  AtlasAdditionalPrompt,
  AtlasChatbotAdditionalPromptsDialogComponent,
  AtlasPromptLanguage,
  AtlasPromptSet,
  AtlasPromptSelection
} from '@secca/shared/components/atlas-chatbot/additional-prompts-dialog/atlas-chatbot-additional-prompts-dialog.component';
import { StakeholderTypeEnum } from '@secca/shared/models/enums';
import {
  FraudDetectionRequest,
  FraudDetectionResponse
} from '@secca/shared/models/atlas/atlas-fraud-detection.model';
import { SettingsService } from '@secca/core/services/settings.service';
import {
  AtlasContextFieldSettings,
  AtlasContextInputsDialogData,
  AtlasTemplateContext,
  ChatMessage
} from '@secca/shared/models/atlas/atlas-chatbot.model';
import { AtlasChatHistorySession } from '@secca/shared/models/atlas/atlas-chat-history.model';
import { extractErrorDetail } from '@secca/shared/helpers/atlas/atlas-errorhandler-helper';
import {
  formatAtlasMessageTimestamp,
  formatDisplayDate
} from '@secca/shared/helpers/atlas/atlas-date-format-helper';
import { toNonEmptyString } from '@secca/shared/helpers/string-helper';
import {
  ADDITIONAL_SUGGESTED_PROMPTS_CONTEXT_OFF,
  ADDITIONAL_SUGGESTED_PROMPTS_CONTEXT_ON
} from '@secca/shared/components/atlas-chatbot/atlas-chatbot-suggested-prompts.constants';
import {
  getHistoryDialogMessages,
  mapApiMessageToChatMessage,
  mapApiSessionToHistorySession,
  mapChatMessageToApiRequest
} from '@secca/shared/helpers/atlas/atlas-chatbot-mappers.helper';
import {
  getChatHistorySessionSortTime,
  isSameChatHistorySession,
  normalizeChatHistorySession,
  normalizeChatMessages
} from '@secca/shared/helpers/atlas/atlas-chat-history-session.helper';
import {
  createBotMessage as createChatBotMessage,
  createInitialMessages as createInitialChatMessages,
  createMessageTimestamp,
  createSystemErrorMessage as createChatSystemErrorMessage,
  getMessageContextTooltip as getChatMessageContextTooltip
} from '@secca/shared/helpers/atlas/atlas-chat-message-factory.helper';
import {
  enforceContextFieldAvailability,
  getEnabledContextFieldLabels,
  normalizeContextFieldSettings
} from '@secca/shared/helpers/atlas/atlas-context-field-settings.helper';
import {
  AtlasCaseNumberLookupService,
  normalizeCaseNumberForLookup
} from '@secca/core/services/atlas/atlas-case-number-lookup.service';
import { parseSuggestedPromptQuestionsCsv } from '@secca/shared/helpers/atlas/atlas-suggested-prompts-parser.helper';
import { AtlasFraudDetectionContextService } from '@secca/core/services/atlas/atlas-fraud-detection-context.service';
import { AtlasCaseContextService } from '@secca/core/services/atlas/atlas-case-context.service';
import { IncidentStateService } from '@secca/core/state-services/incident-state-service';
import { CaseIncidentIdWithCaseId } from '@secca/shared/models/caseIncidentWithCaseId';

@Component({
  selector: 'app-atlas-chatbot',
  templateUrl: './atlas-chatbot.component.html',
  styleUrls: ['./atlas-chatbot.component.scss'],
  standalone: false
})
export class AtlasChatbotComponent implements OnInit, AfterViewChecked {
  private readonly caseContextToggleKey = 'atlas-chat-case-context-enabled';
  private readonly atlasContextFieldSettingsKey = 'atlas-chat-context-field-settings';
  private readonly suggestedPromptQuestionsCsvPath = 'assets/atlas-chatbot/additional-prompts-dialog/suggested_prompt_questions.csv';
  private readonly suggestedPromptQuestionsCaseContextOnCsvPath = 'assets/atlas-chatbot/additional-prompts-dialog/suggested_prompt_questions_case_context_on.csv';
  private readonly defaultSuggestedPromptLanguageByTeamCode: { [teamCode: string]: AtlasPromptLanguage } = {
    ADT04: 'danish',
    ADT06: 'finnish',
    ADT03: 'norwegian',
    ADT34: 'swedish',
    ADT35: 'swedish'
  };
  private readonly chatHistoryPageSize = 10;
  private readonly localPendingResponseSessionId = 'local-atlas-chat-response';
  readonly maxDraftMessageLength = 200;
  readonly maxMessagesPerChatSession = 10;
  private readonly scrollToBottomRetryDelays = [0, 50, 150, 300, 600, 1000];
  private readonly scrollToBottomObserveDurationMs = 1600;
  private shouldScrollToBottom = false;
  private pendingScrollToBottomFrame: number | null = null;
  private pendingScrollToBottomTimeout: number | null = null;
  private pendingScrollToBottomObserveTimeout: number | null = null;
  private scrollToBottomResizeObserver: ResizeObserver | null = null;
  private scrollToBottomRequestId = 0;
  private atlasChatSessionId: string | null = null;
  private atlasPersistedChatSessionId: string | null = null;
  private apiChatHistorySessions: AtlasChatHistorySession[] = [];
  private apiChatHistoryTotal = 0;
  private isLoadingMoreChatHistorySessions = false;
  private removedChatSessionIds = new Set<string>();
  private isInitializingAtlasChatState = false;
  private activeSessionCaseNumber: string | null = null;
  private activeSessionCaseContextModeEnabled = false;
  private legacyActiveSessionCaseNumber: string | null = null;
  private atlasTemplateContext: AtlasTemplateContext | null = null;
  private csvAdditionalSuggestedPromptsContextOff: AtlasAdditionalPrompt[] | null = null;
  private csvAdditionalSuggestedPromptsContextOn: AtlasAdditionalPrompt[] | null = null;
  private chatHistoryDialogRef: MatDialogRef<AtlasChatbotChatHistoryDialogComponent> | null = null;
  private firstContactTemplateDialogRef: MatDialogRef<AtlasChatbotFirstContactTemplateDialogComponent> | null = null;
  private contextInputsDialogRef: MatDialogRef<AtlasChatbotContextInputsDialogComponent> | null = null;
  private blockedCaseChatSessionCreationLimitCaseNumber: string | null = null;
  private blockedGeneralChatSessionCreationLimit = false;
  private chatSessionMessageLimitReachedSessionIds = new Set<string>();
  private loggedInUserTeamCode: string | null = null;
  private currentCaseLoadSeq = 0;
  private fraudDetectionBadgeLoadSeq = 0;
  private pendingResponseSessionId: string | null = null;
  private pendingChatResponses = new Map<string, {
    sessionId: string;
    messages: ChatMessage[];
    caseNumber: string | null;
    sessionDescription: string;
    caseContextModeEnabled: boolean;
    createdAt: string;
  }>();
  chatUsageStatus: AtlasChatUsageStatusResponse | null = null;

  isOpen = true;
  isAtlasEnabledUser = false;
  isAtlasBackendAvailable = true;
  isThinking = false;
  isSendingMessage = false;
  isCreatingNewChat = false;
  useCurrentCaseContext = true;
  isActionRailOpen = true;
  sessionIdDisplay = '-';
  contextFieldSettings: AtlasContextFieldSettings = {
    customerProfile: true,
    productName: true,
    insurancePeriodFrom: true,
    insurancePeriodTo: true,
    reporter: true,
    endUserNationality: true,
    winterSportEnabled: true,
    incidentLocation: true,
    incidentCauseLevel1: true,
    incidentCauseLevel2: true,
    incidentCauseLevel3: true,
    incidentDescription: true,
    mainDiagnosis: true,
    departureDate: true,
    arrivalDate: true,
    travelDestination: true,
    coTravellers: true,
    purposeOfTravel: true,
    incidentDate: true
  };
  draftMessage = '';
  sessionDescription = '';
  isEditingSessionDescription = false;
  sessionDescriptionDraft = '';
  messages: ChatMessage[] = [];
  hasSelectedCase = false;
  currentCaseLabel = 'No case selected';
  currentCaseNumber = '-';
  currentEndUserNationalId: string | null = null;
  activeCommentIndex: number | null = null;
  activeCommentDraft = '';
  hasUnreadBotResponse = false;
  fraudDetectionBadgeScore: number | null = null;
  fraudDetectionBadgeTone: 'green' | 'yellow' | 'orange' | 'red' = 'green';
  fraudDetectionFlagCount = 0;
  isFraudDetectionReviewed = false;
  @ViewChild('chatBody') private chatBody?: ElementRef<HTMLDivElement>;
  @ViewChild('chatInput') private chatInput?: ElementRef<HTMLInputElement>;

  constructor(
    private router: Router,
    private dataService: DataService,
    private caseService: CaseService,
    private incidentService: IncidentService,
    private httpClient: HttpClient,
    public caseLockHelperService: CaseLockHelperService,
    private atlasChatService: AtlasChatService,
    private atlasFraudDetectionService: AtlasFraudDetectionService,
    private keycloakService: KeycloakService,
    private settingsService: SettingsService,
    private dictionaryService: DictionaryService,
    private snackBar: MatSnackBar,
    private dialogHelperUtilService: DialogHelperUtilService,
    private atlasCaseNumberLookupService: AtlasCaseNumberLookupService,
    private atlasFraudDetectionContextService: AtlasFraudDetectionContextService,
    private atlasCaseContextService: AtlasCaseContextService,
    private incidentStateService: IncidentStateService
  ) {}

  ngOnInit(): void {
    this.isAtlasEnabledUser = this.checkAtlasEnabledUser();
    if (!this.isAtlasEnabledUser) {
      return;
    }

    const savedCaseContextToggle = sessionStorage.getItem(this.caseContextToggleKey);
    const savedFieldSettings = sessionStorage.getItem(this.atlasContextFieldSettingsKey);
    this.messages = [];
    this.useCurrentCaseContext = savedCaseContextToggle ? JSON.parse(savedCaseContextToggle) : true;
    this.contextFieldSettings = normalizeContextFieldSettings(savedFieldSettings ? JSON.parse(savedFieldSettings) : null);
    this.updateSessionIdDisplay();
    this.dictionaryService.getLoggedInUser().subscribe(user => {
      this.loggedInUserTeamCode = user?.userTeam?.adtCode || null;
    });
    this.incidentStateService.getIncident().subscribe(incidentState => {
      this.updateAtlasTemplateContextFromIncidentState(incidentState);
    });
    this.initializeAtlasChatState();
    this.router.events.subscribe(event => {
      if (event instanceof NavigationEnd) {
        this.updateCurrentCaseLabel();
      }
    });
    this.shouldScrollToBottom = true;
  }

  ngAfterViewChecked(): void {
    if (!this.shouldScrollToBottom || !this.chatBody?.nativeElement) {
      return;
    }

    this.scheduleScrollToBottom();
  }

  private scheduleScrollToBottom(): void {
    const requestId = ++this.scrollToBottomRequestId;
    this.shouldScrollToBottom = false;
    this.clearPendingScrollToBottomTimers();
    this.disconnectScrollToBottomObserver();

    this.pendingScrollToBottomFrame = window.requestAnimationFrame(() => {
      this.pendingScrollToBottomFrame = null;
      this.observeChatBodySizeForScrollToBottom(requestId);
      this.scrollChatBodyToBottomRepeatedly(requestId, 0);
    });
  }

  cancelScheduledScrollToBottom(): void {
    this.scrollToBottomRequestId++;
    this.shouldScrollToBottom = false;
    this.clearPendingScrollToBottomTimers();
    this.disconnectScrollToBottomObserver();
  }

  private clearPendingScrollToBottomTimers(): void {
    if (this.pendingScrollToBottomFrame != null) {
      window.cancelAnimationFrame(this.pendingScrollToBottomFrame);
      this.pendingScrollToBottomFrame = null;
    }
    if (this.pendingScrollToBottomTimeout != null) {
      window.clearTimeout(this.pendingScrollToBottomTimeout);
      this.pendingScrollToBottomTimeout = null;
    }
    if (this.pendingScrollToBottomObserveTimeout != null) {
      window.clearTimeout(this.pendingScrollToBottomObserveTimeout);
      this.pendingScrollToBottomObserveTimeout = null;
    }
  }

  private observeChatBodySizeForScrollToBottom(requestId: number): void {
    const chatBody = this.chatBody?.nativeElement;
    if (!chatBody || typeof ResizeObserver === 'undefined') {
      return;
    }

    this.scrollToBottomResizeObserver = new ResizeObserver(() => {
      if (requestId !== this.scrollToBottomRequestId) {
        return;
      }

      this.scrollChatBodyToBottom();
    });
    this.scrollToBottomResizeObserver.observe(chatBody);
    Array.from(chatBody.children).forEach(child => {
      this.scrollToBottomResizeObserver?.observe(child);
    });
    this.pendingScrollToBottomObserveTimeout = window.setTimeout(() => {
      if (requestId === this.scrollToBottomRequestId) {
        this.disconnectScrollToBottomObserver();
      }
    }, this.scrollToBottomObserveDurationMs);
  }

  private disconnectScrollToBottomObserver(): void {
    this.scrollToBottomResizeObserver?.disconnect();
    this.scrollToBottomResizeObserver = null;
  }

  private scrollChatBodyToBottomRepeatedly(requestId: number, attempt: number): void {
    if (requestId !== this.scrollToBottomRequestId) {
      return;
    }

    this.scrollChatBodyToBottom();
    const nextAttempt = attempt + 1;
    if (nextAttempt >= this.scrollToBottomRetryDelays.length) {
      this.pendingScrollToBottomTimeout = null;
      return;
    }

    this.pendingScrollToBottomTimeout = window.setTimeout(() => {
      this.scrollChatBodyToBottomRepeatedly(requestId, nextAttempt);
    }, this.scrollToBottomRetryDelays[nextAttempt]);
  }

  private scrollChatBodyToBottom(): void {
    const chatBody = this.chatBody?.nativeElement;
    if (!chatBody) {
      return;
    }

    chatBody.scrollTop = chatBody.scrollHeight;
    if (typeof chatBody.scrollTo === 'function') {
      chatBody.scrollTo({ top: chatBody.scrollHeight, behavior: 'auto' });
    }
  }

  async toggleOpen(): Promise<void> {
    this.isOpen = !this.isOpen;
    if (this.isOpen) {
      this.isActionRailOpen = true;
      this.hasUnreadBotResponse = false;
      this.shouldScrollToBottom = true;
      await this.refreshAtlasChatUsageStatus();
      if (!this.isAtlasBackendAvailable) {
        return;
      }

      await this.synchronizeActiveChatSessionWithCase();
    } else {
      this.isActionRailOpen = false;
    }
  }

  toggleActionRail(): void {
    this.isActionRailOpen = !this.isActionRailOpen;
  }

  async sendMessage(): Promise<void> {
    const trimmedMessage = this.draftMessage.trim();
    if (!this.canSendDraftMessage) {
      return;
    }

    const userMessage: ChatMessage = {
      author: 'user',
      text: trimmedMessage,
      timestamp: createMessageTimestamp(),
      caseContextModeEnabled: this.isCaseContextModeOn,
      caseContextCaseNumber: this.isCaseContextModeOn,
      caseContextCaseNumberValue: this.isCaseContextModeOn && this.currentCaseNumber !== '-'
        ? this.currentCaseNumber
        : null,
      caseContextEnabledFields: this.isCaseContextModeOn ? getEnabledContextFieldLabels(this.contextFieldSettings) : [],
      reaction: null,
      reactionComment: ''
    };
    this.messages = [...this.messages, userMessage];
    const originalMessageIds = new Set((this.messages || [])
      .map(message => message.id)
      .filter((id): id is string => !!id));
    const originalSessionId = this.atlasPersistedChatSessionId;
    const pendingSessionId = originalSessionId || this.localPendingResponseSessionId;
    const pendingCaseNumber = this.getStoredActiveSessionCaseNumber()
      || userMessage.caseContextCaseNumberValue
      || (this.isCaseContextModeOn && this.currentCaseNumber !== '-' ? this.currentCaseNumber : null);
    this.draftMessage = '';
    this.isSendingMessage = true;
    this.isThinking = true;
    this.pendingResponseSessionId = pendingSessionId;
    this.upsertPendingChatResponse(pendingSessionId, {
      messages: this.messages,
      caseNumber: pendingCaseNumber,
      sessionDescription: this.sessionDescription,
      caseContextModeEnabled: this.hasActiveCaseContextMode() || !!userMessage.caseContextModeEnabled
    });
    this.shouldScrollToBottom = true;
    this.persistMessages();

    try {
      await this.applyCaseContextDataToMessage(userMessage);
      this.upsertPendingChatResponse(pendingSessionId, {
        messages: this.messages,
        caseNumber: pendingCaseNumber,
        sessionDescription: this.sessionDescription,
        caseContextModeEnabled: this.hasActiveCaseContextMode() || !!userMessage.caseContextModeEnabled
      });
      const response = await this.sendUserMessageToAtlasChatSession(
        userMessage,
        trimmedMessage,
        originalMessageIds,
        pendingSessionId,
        pendingCaseNumber
      );
      this.assignCaseNumberAfterCaseContextMessage(userMessage, response.chatSessionId);
      this.pendingResponseSessionId = response.chatSessionId;
      const completedMessages = [...this.getPendingChatMessages(response.chatSessionId), response.botMessage];
      this.completePendingChatResponse(response.chatSessionId, completedMessages);
      if (this.isActiveChatSession(response.chatSessionId)) {
        this.isThinking = false;
        this.messages = completedMessages;
      } else {
        await this.refreshApiChatSessions();
      }
    } catch (error) {
      this.markChatSessionMessageLimitReached(error, this.pendingResponseSessionId || pendingSessionId);
      const errorMessage = this.createSystemErrorMessage(this.getAtlasChatSendErrorMessage(error));
      const failedSessionId = this.pendingResponseSessionId || pendingSessionId;
      const failedMessages = [...this.getPendingChatMessages(failedSessionId), errorMessage];
      this.completePendingChatResponse(failedSessionId, failedMessages);
      if (this.isActiveChatSession(failedSessionId)) {
        this.isThinking = false;
        this.messages = failedMessages;
      }
    } finally {
      await this.refreshAtlasChatUsageStatus();
      this.isThinking = false;
      this.isSendingMessage = false;
      if (!this.hasActivePendingChatResponse) {
        this.pendingResponseSessionId = null;
      }
      this.shouldScrollToBottom = true;
      this.persistMessages();
    }
  }

  private persistMessages(): void {
    this.refreshOpenChatHistoryDialogSessions();
  }

  async clearChat(): Promise<void> {
    if (!this.isAtlasBackendAvailable || this.isCreatingNewChat || !this.canStartNewAtlasChat) {
      return;
    }

    await this.createNewChatForCase(true);
  }

  setMessageReaction(messageIndex: number, reaction: 'like' | 'dislike'): void {
    const current = this.messages[messageIndex];
    if (!current || current.author !== 'bot') {
      return;
    }

    const nextReaction = current.reaction === reaction ? null : reaction;
    const updatedMessages = [...this.messages];
    updatedMessages[messageIndex] = { ...current, reaction: nextReaction };
    this.messages = updatedMessages;
    this.persistMessages();
    this.updateChatMessageFeedbackInApi(updatedMessages[messageIndex]);
  }

  startEditMessageComment(messageIndex: number): void {
    const current = this.messages[messageIndex];
    if (!current || current.author !== 'bot') {
      return;
    }

    this.activeCommentIndex = messageIndex;
    this.activeCommentDraft = current.reactionComment || '';
  }

  updateActiveCommentDraft(comment: string): void {
    this.activeCommentDraft = comment;
  }

  saveMessageComment(messageIndex: number): void {
    const current = this.messages[messageIndex];
    if (!current || current.author !== 'bot') {
      return;
    }

    current.reactionComment = this.activeCommentDraft.trim();
    this.persistMessages();
    this.updateChatMessageFeedbackInApi(current);
    this.activeCommentIndex = null;
    this.activeCommentDraft = '';
  }

  onCommentInputEnter(event: KeyboardEvent, messageIndex: number): void {
    event.preventDefault();
    this.saveMessageComment(messageIndex);
  }

  toggleCaseContext(): void {
    if (this.isCaseContextToggleDisabled) {
      return;
    }

    this.useCurrentCaseContext = !this.useCurrentCaseContext;
    sessionStorage.setItem(this.caseContextToggleKey, JSON.stringify(this.useCurrentCaseContext));
    if (!this.useCurrentCaseContext && !this.hasConversationContent()) {
      this.activeSessionCaseNumber = null;
      this.updateActiveChatSessionMetadataInApi();
    }
    this.refreshOpenChatHistoryDialogSessions();
  }

  applySuggestedPrompt(prompt: string): void {
    if (!this.isAtlasBackendAvailable
      || this.hasBlockedChatSessionCreationLimit
      || this.hasActiveChatSessionMessageLimitReached
      || !this.canSendMoreAtlasMessages) {
      return;
    }

    this.draftMessage = prompt || '';
    window.setTimeout(() => {
      this.chatInput?.nativeElement?.focus();
    });
  }

  applySuggestedPromptWithAnimation(selection: AtlasPromptSelection): void {
    if (!selection?.prompt) {
      return;
    }

    const targetRect = this.chatInput?.nativeElement?.getBoundingClientRect();
    if (!selection.sourceRect || !targetRect || !document.body?.animate) {
      this.applySuggestedPrompt(selection.prompt);
      return;
    }

    const sourceRect = selection.sourceRect;
    const flyer = document.createElement('div');
    flyer.textContent = selection.prompt;
    Object.assign(flyer.style, {
      position: 'fixed',
      left: `${sourceRect.left}px`,
      top: `${sourceRect.top}px`,
      width: `${Math.min(sourceRect.width, 360)}px`,
      maxWidth: '360px',
      minHeight: '32px',
      padding: '9px 12px',
      border: '1px solid #8fc3d4',
      borderRadius: '8px',
      background: '#ffffff',
      color: '#245b6c',
      boxShadow: '0 14px 34px rgba(20, 73, 92, 0.22)',
      fontSize: '12px',
      lineHeight: '1.35',
      fontFamily: 'SemiBold, Effra, Arial, sans-serif',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      pointerEvents: 'none',
      zIndex: '2147483647',
      transformOrigin: 'left top'
    });
    document.body.appendChild(flyer);

    const startX = sourceRect.left;
    const startY = sourceRect.top;
    const endX = targetRect.left + 12;
    const endY = targetRect.top + Math.max(4, (targetRect.height - 32) / 2);
    const deltaX = endX - startX;
    const deltaY = endY - startY;

    const animation = flyer.animate([
      {
        transform: 'translate3d(0, 0, 0) scale(1)',
        opacity: 1,
        offset: 0
      },
      {
        transform: `translate3d(${deltaX * 0.52}px, ${deltaY - 34}px, 0) scale(0.92)`,
        opacity: 0.96,
        offset: 0.58
      },
      {
        transform: `translate3d(${deltaX}px, ${deltaY}px, 0) scale(0.24)`,
        opacity: 0.1,
        offset: 1
      }
    ], {
      duration: 520,
      easing: 'cubic-bezier(.2,.9,.2,1)',
      fill: 'forwards'
    });

    let promptApplied = false;
    const applyPromptOnce = () => {
      if (promptApplied) {
        return;
      }

      promptApplied = true;
      this.applySuggestedPrompt(selection.prompt);
    };

    window.setTimeout(applyPromptOnce, 180);

    animation.onfinish = () => {
      flyer.remove();
      applyPromptOnce();
    };
    animation.oncancel = () => {
      flyer.remove();
      applyPromptOnce();
    };
  }

  get areAtlasCaseFeaturesDisabled(): boolean {
    return !this.isAtlasBackendAvailable || !this.hasSelectedCase || this.caseLockHelperService.isCaseBasicLockTaken;
  }

  get hasRequiredTemplateCauseLevels(): boolean {
    return !!toNonEmptyString(this.atlasTemplateContext?.incidentCauseLevel1Code) &&
      !!toNonEmptyString(this.atlasTemplateContext?.incidentCauseLevel2Code);
  }

  get isFirstContactTemplateDisabled(): boolean {
    return this.areAtlasCaseFeaturesDisabled || !this.hasRequiredTemplateCauseLevels;
  }

  get firstContactTemplateTooltip(): string {
    return this.hasSelectedCase && !this.hasRequiredTemplateCauseLevels
      ? 'First contact requires cause level 1 and 2'
      : 'First contact';
  }

  get hasFraudDetectionBadge(): boolean {
    return this.fraudDetectionBadgeScore != null;
  }

  get hasElevatedFraudDetectionBadge(): boolean {
    return !this.isFraudDetectionReviewed && this.fraudDetectionBadgeScore != null && (this.fraudDetectionBadgeTone === 'red'
      || this.fraudDetectionBadgeTone === 'orange' );
  }

  get fraudDetectionBadgeText(): string {
    return this.fraudDetectionBadgeScore == null ? '' : `${this.fraudDetectionBadgeScore}`;
  }

  get fraudDetectionBadgeLabel(): string {
    switch (this.fraudDetectionBadgeTone) {
      case 'red':
        return 'Fraud risk';
      case 'orange':
        return 'Elevated fraud risk';
      case 'yellow':
        return 'Moderate fraud risk';
      default:
        return 'Low fraud risk';
    }
  }

  get hasFraudDetectionFlagCount(): boolean {
    return !this.isFraudDetectionReviewed && this.fraudDetectionFlagCount > 0;
  }

  get hasBlockedNationalIdInput(): boolean {
    const nationalId = this.normalizeNationalId(this.currentEndUserNationalId);
    const rawMessage = this.draftMessage || '';
    const normalizedMessage = this.normalizeNationalId(rawMessage);

    if (this.containsNationalIdPattern(rawMessage)) {
      return true;
    }

    if (!nationalId || nationalId.length < 4 || !normalizedMessage) {
      return false;
    }

    return normalizedMessage.includes(nationalId);
  }

  get isDraftMessageTooLong(): boolean {
    return this.draftMessageLength > this.maxDraftMessageLength;
  }

  get draftMessageLength(): number {
    return (this.draftMessage || '').length;
  }

  get canSendDraftMessage(): boolean {
    return !!this.draftMessage.trim()
      && this.isAtlasBackendAvailable
      && !this.hasActivePendingChatResponse
      && !this.isCreatingNewChat
      && this.canSendMoreAtlasMessages
      && !this.hasBlockedChatSessionCreationLimit
      && !this.hasBlockedMismatchedCaseContext
      && !this.hasActiveChatSessionMessageLimitReached
      && !this.hasBlockedNationalIdInput
      && !this.isDraftMessageTooLong;
  }

  get isThinkingVisible(): boolean {
    return this.hasActivePendingChatResponse;
  }

  get hasActivePendingChatResponse(): boolean {
    return !!this.getActivePendingChatResponse();
  }

  get canCreateAtlasChatSession(): boolean {
    return this.chatUsageStatus?.can_create_chat_session !== false;
  }

  get canStartNewAtlasChat(): boolean {
    return this.canCreateAtlasChatSession && this.hasSentUserMessage();
  }

  get canSendMoreAtlasMessages(): boolean {
    return this.chatUsageStatus?.can_send_more_messages !== false;
  }

  get hasAtlasChatSessionCreationLimitReached(): boolean {
    return this.chatUsageStatus?.can_create_chat_session === false;
  }

  get hasAtlasChatMessageLimitReached(): boolean {
    return this.chatUsageStatus?.can_send_more_messages === false;
  }

  get hasActiveChatSessionMessageLimitReached(): boolean {
    return this.sentUserMessageCount >= this.maxMessagesPerChatSession
      || (!!this.atlasPersistedChatSessionId
        && this.chatSessionMessageLimitReachedSessionIds.has(this.atlasPersistedChatSessionId));
  }

  get hasBlockedCaseChatSessionCreationLimit(): boolean {
    return this.hasAtlasChatSessionCreationLimitReached
      && !!this.blockedCaseChatSessionCreationLimitCaseNumber
      && this.hasSelectedCase
      && this.currentCaseNumber !== '-'
      && this.blockedCaseChatSessionCreationLimitCaseNumber === this.currentCaseNumber;
  }

  get hasBlockedGeneralChatSessionCreationLimit(): boolean {
    return this.hasAtlasChatSessionCreationLimitReached
      && this.blockedGeneralChatSessionCreationLimit
      && !this.hasSelectedCase;
  }

  get hasBlockedChatSessionCreationLimit(): boolean {
    return this.hasBlockedCaseChatSessionCreationLimit || this.hasBlockedGeneralChatSessionCreationLimit;
  }

  get chatSessionCreationLimitTitle(): string {
    return this.hasBlockedCaseChatSessionCreationLimit
      ? 'Atlas chat is unavailable for this case'
      : 'Atlas chat is unavailable';
  }

  get chatSessionCreationLimitText(): string {
    const limitText = this.atlasChatSessionCreationLimitText.replace(/^You have/, 'you have');
    if (this.hasBlockedCaseChatSessionCreationLimit) {
      return `No existing Atlas chat can be continued for case ${this.currentCaseNumber}, and ${limitText}`;
    }

    return `No existing Atlas chat can be continued from the overview, and ${limitText}`;
  }

  get atlasChatSessionCreationLimitText(): string {
    const limit = this.chatUsageStatus?.chat_session_creation_limit_per_hour;
    const used = this.chatUsageStatus?.chat_sessions_created_by_user_last_hour;
    if (limit == null || limit <= 0 || used == null) {
      return 'You have reached the hourly limit for creating new Atlas chat sessions.';
    }

    return `You have reached the hourly Atlas chat session limit (${used}/${limit}). Try again later.`;
  }

  get atlasChatMessageLimitText(): string {
    const limit = this.chatUsageStatus?.user_message_creation_limit_per_day;
    const used = this.chatUsageStatus?.user_messages_created_by_user_last_day;
    if (limit == null || limit <= 0 || used == null) {
      return 'You have reached the daily limit for sending Atlas chat messages.';
    }

    return `You have reached the daily Atlas message limit (${used}/${limit}). Try again later.`;
  }

  get atlasChatSessionMessageLimitText(): string {
    return `This chat session has reached the limit of ${this.maxMessagesPerChatSession} messages. Start a new chat to continue.`;
  }

  get activeChatCaseLabel(): string {
    if (!this.hasSelectedCase) {
      return this.currentCaseLabel;
    }

    const activeCaseNumber = this.getStoredActiveSessionCaseNumber();
    if (!activeCaseNumber) {
      return this.currentCaseLabel;
    }

    if (activeCaseNumber === this.currentCaseNumber) {
      return this.currentCaseLabel;
    }

    return activeCaseNumber;
  }

  get hasBlockedMismatchedCaseContext(): boolean {
    const activeCaseNumber = this.getStoredActiveSessionCaseNumber();
    return this.isCaseContextModeOn
      && !!activeCaseNumber
      && this.hasSelectedCase
      && this.currentCaseNumber !== '-'
      && activeCaseNumber !== this.currentCaseNumber;
  }

  get isCaseContextModeOn(): boolean {
    return this.hasSelectedCase
      && this.currentCaseNumber !== '-'
      && (this.useCurrentCaseContext || this.activeSessionCaseContextModeEnabled);
  }

  get isCaseContextToggleDisabled(): boolean {
    return !this.hasSelectedCase || this.activeSessionCaseContextModeEnabled;
  }

  get mismatchedCaseContextText(): string {
    return `This chat is linked to case ${this.activeChatCaseLabel}. Start or open a chat for the selected case before sending messages with case context.`;
  }

  get sentUserMessageCount(): number {
    return (this.messages || [])
      .filter(message => message.author === 'user')
      .length;
  }

  onSessionDescriptionChange(value: string): void {
    this.sessionDescription = value || '';
    if (this.atlasPersistedChatSessionId) {
      this.atlasChatService.updateAtlasChatSession(this.atlasPersistedChatSessionId, {
        session_description: this.sessionDescription || null
      }).subscribe({
        next: () => this.refreshApiChatSessions(),
        error: () => {}
      });
    }
  }

  openFirstContactTemplateDialog(): void {
    if (this.isFirstContactTemplateDisabled) {
      return;
    }

    this.firstContactTemplateDialogRef = this.dialogHelperUtilService.open(AtlasChatbotFirstContactTemplateDialogComponent, {
      id: 'AtlasFirstContactTemplateDialog',
      width: '900px',
      position: {},
      hasBackdrop: false,
      data: this.atlasTemplateContext
    });

    this.firstContactTemplateDialogRef?.afterClosed().subscribe(() => {
      this.firstContactTemplateDialogRef = null;
    });
  }

  onVoiceToTextSummaryClick(): void {
    if (this.areAtlasCaseFeaturesDisabled) {
      return;
    }

    this.snackBar.open('Voice-to-text summary is not available yet.', 'Close', {
      duration: 3000,
      horizontalPosition: 'right',
      panelClass: ['atlas-chat-snackbar'],
      verticalPosition: 'bottom'
    });
  }

  onChatBodyClick(event: MouseEvent): void {
    const target = event.target as Element | null;
    const link = target?.closest('a') as HTMLAnchorElement | null;
    const href = link?.href;

    if (!href) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();
    window.open(href, '_blank', 'noopener,noreferrer');
  }

  async onFraudDetectionClick(): Promise<void> {
    if (this.areAtlasCaseFeaturesDisabled) {
      return;
    }

    const data = await this.buildFraudDetectionDialogData();
    data.onFraudDetectionLoaded = result => this.applyFraudDetectionBadgeResult(result);
    data.onReviewCompleted = () => this.handleFraudDetectionReviewCompleted();

    this.dialogHelperUtilService.open(AtlasFraudDetectionDialogComponent, {
      id: 'AtlasFraudDetectionDialog',
      width: '760px',
      position: {},
      hasBackdrop: false,
      data
    });
  }

  openInfoDialog(): void {
    this.dialogHelperUtilService.open(AtlasChatbotInfoDialogComponent, {
      id: 'AtlasInfoDialog',
      width: '720px',
      position: {},
      hasBackdrop: false
    });
  }

  async openAdditionalPromptsDialog(): Promise<void> {
    if (this.hasBlockedChatSessionCreationLimit
      || this.hasActiveChatSessionMessageLimitReached
      || !this.canSendMoreAtlasMessages) {
      return;
    }

    const [generalPrompts, casePrompts] = await Promise.all([
      this.getCsvAdditionalSuggestedPromptsContextOff(),
      this.getCsvAdditionalSuggestedPromptsContextOn()
    ]);
    const defaultPromptSet: AtlasPromptSet = this.isCaseContextModeOn ? 'case' : 'general';
    const dialogRef = this.dialogHelperUtilService.open(AtlasChatbotAdditionalPromptsDialogComponent, {
      id: 'AtlasAdditionalPromptsDialog',
      width: '620px',
      position: {},
      hasBackdrop: false,
      data: {
        prompts: defaultPromptSet === 'case' ? casePrompts : generalPrompts,
        generalPrompts,
        casePrompts,
        defaultPromptSet,
        defaultLanguage: await this.getDefaultAdditionalPromptLanguage()
      }
    });

    dialogRef?.componentInstance?.promptSelected?.subscribe((selection: AtlasPromptSelection) => {
      this.applySuggestedPromptWithAnimation(selection);
    });
  }

  private async getDefaultAdditionalPromptLanguage(): Promise<AtlasPromptLanguage> {
    if (this.loggedInUserTeamCode) {
      return this.getAdditionalPromptLanguageForTeamCode(this.loggedInUserTeamCode);
    }

    try {
      const user = await firstValueFrom(this.dictionaryService.getLoggedInUser().pipe(
        filter(loggedInUser => !!loggedInUser),
        take(1),
        timeout(1000)
      ));
      this.loggedInUserTeamCode = user?.userTeam?.adtCode || null;
    } catch {
      this.loggedInUserTeamCode = null;
    }

    return this.getAdditionalPromptLanguageForTeamCode(this.loggedInUserTeamCode);
  }

  private getAdditionalPromptLanguageForTeamCode(teamCode: string | null | undefined): AtlasPromptLanguage {
    return this.defaultSuggestedPromptLanguageByTeamCode[teamCode || ''] || 'danish';
  }

  private async getCsvAdditionalSuggestedPromptsContextOn(): Promise<AtlasAdditionalPrompt[]> {
    if (this.csvAdditionalSuggestedPromptsContextOn) {
      return this.csvAdditionalSuggestedPromptsContextOn;
    }

    this.csvAdditionalSuggestedPromptsContextOn = await this.getCsvAdditionalSuggestedPrompts(
      this.suggestedPromptQuestionsCaseContextOnCsvPath,
      ADDITIONAL_SUGGESTED_PROMPTS_CONTEXT_ON
    );

    return this.csvAdditionalSuggestedPromptsContextOn;
  }

  private async getCsvAdditionalSuggestedPromptsContextOff(): Promise<AtlasAdditionalPrompt[]> {
    if (this.csvAdditionalSuggestedPromptsContextOff) {
      return this.csvAdditionalSuggestedPromptsContextOff;
    }

    this.csvAdditionalSuggestedPromptsContextOff = await this.getCsvAdditionalSuggestedPrompts(
      this.suggestedPromptQuestionsCsvPath,
      ADDITIONAL_SUGGESTED_PROMPTS_CONTEXT_OFF
    );

    return this.csvAdditionalSuggestedPromptsContextOff;
  }

  private async getCsvAdditionalSuggestedPrompts(
    csvPath: string,
    fallbackPrompts: AtlasAdditionalPrompt[]
  ): Promise<AtlasAdditionalPrompt[]> {
    try {
      const csvText = await firstValueFrom(this.httpClient.get(csvPath, { responseType: 'text' }));
      const prompts = parseSuggestedPromptQuestionsCsv(csvText);
      return prompts.length ? prompts : fallbackPrompts;
    } catch {
      return fallbackPrompts;
    }
  }

  async onDocumentTranslationClick(): Promise<void> {
    if (this.areAtlasCaseFeaturesDisabled) {
      return;
    }

    const suggestedPiiEntities = await this.atlasCaseContextService.getCurrentEndUserPiiSuggestions(
      this.getActiveCaseId(),
      this.currentCaseNumber
    );

    this.dialogHelperUtilService.open(DocumentTranslationsDialogComponent, {
      id: 'DocumentTranslationsDialog',
      width: '920px',
      position: {},
      hasBackdrop: false,
      data: {
        caseNumber: this.currentCaseNumber,
        caseLabel: this.currentCaseLabel,
        suggestedPiiEntities
      }
    });
  }

  onDocumentAnonymizationClick(): void {
    if (this.areAtlasCaseFeaturesDisabled) {
      return;
    }
  }

  async onCaseValidationClick(): Promise<void> {
    if (this.areAtlasCaseFeaturesDisabled) {
      return;
    }

    const data = await this.buildCaseValidationDialogData();

    this.dialogHelperUtilService.open(AtlasCaseValidationDialogComponent, {
      id: 'AtlasCaseValidationDialog',
      width: '760px',
      position: {},
      hasBackdrop: false,
      data
    });
  }

  async openChatHistoryDialog(): Promise<void> {
    if (!this.isAtlasBackendAvailable) {
      return;
    }

    await this.loadAtlasChatSessionsFromApi({ reset: true });
    const sessions = this.getChatHistoryDialogSessions();
    this.chatHistoryDialogRef = this.dialogHelperUtilService.open(AtlasChatbotChatHistoryDialogComponent, {
      id: 'AtlasChatHistoryDialog',
      width: '1080px',
      position: {},
      hasBackdrop: false,
      data: {
        sessions,
        preferredUsername: this.getPreferredUsername(),
        hasMoreSessions: this.hasMoreApiChatHistorySessions(),
        isLoadingMoreSessions: this.isLoadingMoreChatHistorySessions
      }
    });

    this.chatHistoryDialogRef?.componentInstance?.continueSession?.subscribe((session: AtlasChatHistorySession) => {
      this.continueChatHistorySession(session);
    });

    this.chatHistoryDialogRef?.componentInstance?.loadMoreSessions?.subscribe(() => {
      this.loadMoreChatHistorySessions();
    });

    this.chatHistoryDialogRef?.componentInstance?.sessionChanged?.subscribe(update => {
      this.applyChatHistorySessionChange(update.sessionId, update.changes);
    });

    this.chatHistoryDialogRef?.componentInstance?.sessionRemoved?.subscribe(sessionId => {
      this.removeStoredChatHistorySession(sessionId);
    });

    this.chatHistoryDialogRef?.afterClosed().subscribe(() => {
      this.chatHistoryDialogRef = null;
    });
  }

  async openContextInputsDialog(): Promise<void> {
    if (this.areAtlasCaseFeaturesDisabled) {
      return;
    }

    const data = await this.buildContextInputsDialogData();

    this.contextInputsDialogRef = this.dialogHelperUtilService.open(AtlasChatbotContextInputsDialogComponent, {
      id: 'AtlasContextInputsDialog',
      width: '780px',
      position: {},
      hasBackdrop: false,
      data
    });

    this.contextInputsDialogRef?.componentInstance?.settingsChanged?.subscribe((updatedSettings: AtlasContextFieldSettings) => {
      this.persistContextFieldSettings(updatedSettings);
    });

    this.contextInputsDialogRef?.afterClosed().subscribe((updatedSettings: AtlasContextFieldSettings | undefined) => {
      if (!updatedSettings) {
        this.contextInputsDialogRef = null;
      } else {
        this.persistContextFieldSettings(updatedSettings);
      }
      this.contextInputsDialogRef = null;
    });
  }

  private persistContextFieldSettings(updatedSettings: AtlasContextFieldSettings): void {
    this.contextFieldSettings = { ...updatedSettings };
    sessionStorage.setItem(this.atlasContextFieldSettingsKey, JSON.stringify(this.contextFieldSettings));
  }

  private createInitialMessages(): ChatMessage[] {
    return createInitialChatMessages(this.getLoggedInUserName());
  }

  private createBotMessage(html: string): ChatMessage {
    const shouldHighlightWhenOpened = !this.isOpen;
    if (shouldHighlightWhenOpened) {
      this.hasUnreadBotResponse = true;
    }

    return createChatBotMessage(html);
  }

  private createSystemErrorMessage(text: string): ChatMessage {
    return createChatSystemErrorMessage(text);
  }

  private getAtlasChatSendErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const detail = extractErrorDetail(error.error);
      if (detail) {
        return detail;
      }
    }

    return 'Unable to retrieve response from Atlas chat service.';
  }

  private markChatSessionMessageLimitReached(error: unknown, sessionId: string | null | undefined): void {
    if (!sessionId
      || sessionId === this.localPendingResponseSessionId
      || !this.isChatSessionMessageLimitError(error)) {
      return;
    }

    this.chatSessionMessageLimitReachedSessionIds.add(sessionId);
  }

  private isChatSessionMessageLimitError(error: unknown): boolean {
    if (!(error instanceof HttpErrorResponse) || error.status !== 429) {
      return false;
    }

    const detail = extractErrorDetail(error.error) || '';
    return /session|conversation|chat/i.test(detail)
      && /limit|max|maximum|too many/i.test(detail)
      && /message/i.test(detail);
  }

  private getAtlasChatSessionCreationErrorMessage(error: unknown): string {
    if (error instanceof HttpErrorResponse) {
      const detail = extractErrorDetail(error.error);
      if (detail) {
        return detail;
      }
    }

    return 'Unable to create a new Atlas chat session.';
  }

  private showAtlasChatSessionCreationError(error: unknown): void {
    this.snackBar.open(this.getAtlasChatSessionCreationErrorMessage(error), 'Close', {
      duration: 5000,
      horizontalPosition: 'right',
      panelClass: ['atlas-chat-snackbar'],
      verticalPosition: 'bottom'
    });
  }

  private throwIfHttpError<T>(response: T): T {
    if (response instanceof HttpErrorResponse) {
      throw response;
    }

    return response;
  }

  private getActiveCaseId(): string | null {
    const caseRouteMatch = this.router.url.match(/\/case\/([^/?#]+)/);
    return caseRouteMatch?.[1] || null;
  }

  private async updateCurrentCaseLabel(): Promise<void> {
    const loadSeq = ++this.currentCaseLoadSeq;
    const caseId = this.getActiveCaseId();
    if (!caseId) {
      this.hasSelectedCase = false;
      this.currentCaseLabel = 'No case selected';
      this.currentCaseNumber = '-';
      this.currentEndUserNationalId = null;
      this.useCurrentCaseContext = false;
      sessionStorage.setItem(this.caseContextToggleKey, JSON.stringify(this.useCurrentCaseContext));
      this.atlasTemplateContext = null;
      this.clearFraudDetectionBadge();
      if (!this.isInitializingAtlasChatState) {
        await this.synchronizeActiveChatSessionWithOverview();
      }
      await this.refreshOpenCaseDialogs();
      return;
    }

    this.clearFraudDetectionBadge();

    try {
      const activeCase = await firstValueFrom(this.caseService.getCase(caseId));
      if (loadSeq !== this.currentCaseLoadSeq) {
        return;
      }
      this.hasSelectedCase = true;
      this.currentCaseLabel = this.formatCurrentCaseLabel(activeCase);
      this.currentCaseNumber = activeCase?.caseNumber ? String(activeCase.caseNumber) : String(activeCase?.id || '-');
      this.refreshFraudDetectionBadgeFromCaseNumber(this.currentCaseNumber);
      this.currentEndUserNationalId = await this.atlasCaseContextService.getCurrentEndUserNationalId(caseId);
      if (loadSeq !== this.currentCaseLoadSeq) {
        return;
      }
      await this.updateAtlasTemplateContext(activeCase);
      if (loadSeq !== this.currentCaseLoadSeq) {
        return;
      }
    } catch {
      if (loadSeq !== this.currentCaseLoadSeq) {
        return;
      }
      this.hasSelectedCase = true;
      this.currentCaseLabel = `${caseId}`;
      this.currentCaseNumber = String(caseId);
      this.currentEndUserNationalId = await this.atlasCaseContextService.getCurrentEndUserNationalId(caseId);
      if (loadSeq !== this.currentCaseLoadSeq) {
        return;
      }
      this.atlasTemplateContext = null;
      this.clearFraudDetectionBadge();
    }
    if (!this.isInitializingAtlasChatState) {
      await this.synchronizeActiveChatSessionWithCase();
    }
    await this.refreshOpenCaseDialogs();
  }

  private async buildContextInputsDialogData(): Promise<AtlasContextInputsDialogData> {
    const data = await this.atlasCaseContextService.buildContextInputsDialogData(
      this.getActiveCaseId(),
      this.currentCaseNumber,
      this.contextFieldSettings
    );
    this.persistContextFieldSettings(data.settings);

    return data;
  }

  private async buildCaseValidationDialogData(): Promise<AtlasCaseValidationDialogData> {
    return this.atlasCaseContextService.buildCaseValidationDialogData(this.getActiveCaseId(), this.currentCaseNumber);
  }

  private async buildFraudDetectionDialogData(): Promise<AtlasFraudDetectionDialogData> {
    return this.atlasFraudDetectionContextService.buildDialogData(this.getActiveCaseId(), this.currentCaseNumber);
  }

  private refreshFraudDetectionBadgeFromCaseNumber(caseNumber: string | null | undefined): void {
    const normalizedCaseNumber = normalizeCaseNumberForLookup(caseNumber);
    const loadSeq = ++this.fraudDetectionBadgeLoadSeq;
    this.fraudDetectionBadgeScore = null;
    this.isFraudDetectionReviewed = false;

    if (!this.isAtlasBackendAvailable || !this.hasSelectedCase || !normalizedCaseNumber) {
      return;
    }

    firstValueFrom(this.atlasFraudDetectionService.getLatestFraudDetectionForCase(normalizedCaseNumber))
      .then(result => {
        if (loadSeq !== this.fraudDetectionBadgeLoadSeq) {
          return;
        }

        this.applyFraudDetectionBadgeResult(result);
      })
      .catch(error => {
        if (loadSeq !== this.fraudDetectionBadgeLoadSeq) {
          return;
        }

        if (error instanceof HttpErrorResponse && error.status === 404) {
          void this.refreshFraudDetectionBadge(false);
          return;
        }

        this.fraudDetectionBadgeScore = null;
        this.isFraudDetectionReviewed = false;
      });
  }

  private async refreshFraudDetectionBadge(preferLatestFraudDetection = true): Promise<void> {
    const loadSeq = ++this.fraudDetectionBadgeLoadSeq;
    this.fraudDetectionBadgeScore = null;
    this.isFraudDetectionReviewed = false;

    if (this.areAtlasCaseFeaturesDisabled) {
      return;
    }

    try {
      const data = await this.buildFraudDetectionDialogData();
      if (loadSeq !== this.fraudDetectionBadgeLoadSeq
        || !this.atlasFraudDetectionContextService.hasFraudDetectionRequestInput(data.request)) {
        return;
      }

      const result = await this.getOrCreateFraudDetectionBadgeResult(data.request, preferLatestFraudDetection);
      if (loadSeq !== this.fraudDetectionBadgeLoadSeq) {
        return;
      }

      this.applyFraudDetectionBadgeResult(result);
    } catch {
      if (loadSeq === this.fraudDetectionBadgeLoadSeq) {
        this.fraudDetectionBadgeScore = null;
        this.isFraudDetectionReviewed = false;
      }
    }
  }

  private async getOrCreateFraudDetectionBadgeResult(
    request: Partial<FraudDetectionRequest>,
    preferLatestFraudDetection = true
  ): Promise<FraudDetectionResponse> {
    const caseNumber = request.case_number;
    if (preferLatestFraudDetection && caseNumber) {
      try {
        return await firstValueFrom(this.atlasFraudDetectionService.getLatestFraudDetectionForCase(caseNumber));
      } catch (error) {
        if (!(error instanceof HttpErrorResponse) || error.status !== 404) {
          throw error;
        }
      }
    }

    return firstValueFrom(this.atlasFraudDetectionService.detectFraud(request as FraudDetectionRequest));
  }

  private applyFraudDetectionBadgeResult(result: FraudDetectionResponse | null): void {
    const score = Number(result?.total_score);
    if (!Number.isFinite(score)) {
      this.fraudDetectionBadgeScore = null;
      return;
    }

    this.fraudDetectionBadgeScore = score;
    this.isFraudDetectionReviewed = result?.reviewed === true;
    this.fraudDetectionBadgeTone = this.atlasFraudDetectionContextService.normalizeFraudScoreTone(result?.color) || 'green';
    this.fraudDetectionFlagCount = this.atlasFraudDetectionContextService.getFraudDetectionFlagCount(result);
  }

  private handleFraudDetectionReviewCompleted(): void {
    this.isFraudDetectionReviewed = true;
    this.fraudDetectionFlagCount = 0;
    this.fraudDetectionBadgeTone = 'green';
  }

  private clearFraudDetectionBadge(): void {
    this.fraudDetectionBadgeLoadSeq++;
    this.fraudDetectionBadgeScore = null;
    this.fraudDetectionFlagCount = 0;
    this.isFraudDetectionReviewed = false;
  }

  private async refreshOpenCaseDialogs(): Promise<void> {
    if (this.firstContactTemplateDialogRef?.componentInstance) {
      this.firstContactTemplateDialogRef.componentInstance.applyContext(this.atlasTemplateContext);
    }

    if (this.contextInputsDialogRef?.componentInstance) {
      const data = await this.buildContextInputsDialogData();
      this.contextInputsDialogRef.componentInstance.applyCaseData(
        data.caseNumber,
        data.values,
        data.settings
      );
    }
  }

  private formatCurrentCaseLabel(activeCase: Case): string {
    const primaryId = activeCase.caseNumber ? `${activeCase.caseNumber}` : `Case ${activeCase.id}`;
    const secondaryContext = activeCase.endUserName || activeCase.cause;

    return secondaryContext ? `${primaryId} - ${secondaryContext}` : primaryId;
  }

  getMessageContextTooltip(message: ChatMessage): string | null {
    return getChatMessageContextTooltip(message);
  }

  formatMessageTimestamp(value: string | null | undefined): string {
    return formatAtlasMessageTimestamp(value);
  }

  private getLoggedInUserName(): string {
    const keycloak = this.keycloakService.getKeycloakInstance();
    const parsedToken = keycloak?.tokenParsed as { name?: string; preferred_username?: string; given_name?: string } | undefined;

    return parsedToken?.name || parsedToken?.preferred_username || parsedToken?.given_name || this.keycloakService.getUsername() || 'user';
  }

  private checkAtlasEnabledUser(): boolean {
    const keycloak = this.keycloakService.getKeycloakInstance();
    const parsedToken = keycloak?.tokenParsed as { realm_access?: { roles?: string[] } } | undefined;
    const atlasAccessRole = this.settingsService.getAtlasAccessRole().trim();

    if (!atlasAccessRole) {
      return false;
    }

    return (parsedToken?.realm_access?.roles || []).includes(atlasAccessRole);
  }


  private async ensurePersistedChatSessionForSend(
    pendingSessionId: string,
    caseNumberOverride: string | null
  ): Promise<string | null> {
    if (pendingSessionId !== this.localPendingResponseSessionId) {
      return pendingSessionId;
    }

    const created = this.throwIfHttpError(await firstValueFrom(this.atlasChatService.createAtlasChatSession(
      this.createAtlasChatSessionRequest(caseNumberOverride)
    )));
    const mapped = mapApiSessionToHistorySession(created);
    this.movePendingChatResponse(pendingSessionId, mapped.id, {
      caseNumber: normalizeCaseNumberForLookup(mapped.caseNumber) ? mapped.caseNumber : caseNumberOverride,
      sessionDescription: mapped.sessionDescription || this.sessionDescription,
      caseContextModeEnabled: !!mapped.caseContextModeEnabled || !!this.pendingChatResponses.get(pendingSessionId)?.caseContextModeEnabled
    });
    this.upsertApiChatHistorySession(mapped);
    if (this.isActiveChatSession(pendingSessionId)) {
      this.atlasPersistedChatSessionId = mapped.id;
      this.atlasChatSessionId = mapped.id;
      this.activeSessionCaseNumber = normalizeCaseNumberForLookup(mapped.caseNumber) ? mapped.caseNumber : caseNumberOverride;
      this.activeSessionCaseContextModeEnabled = !!mapped.caseContextModeEnabled || this.activeSessionCaseContextModeEnabled;
      this.updateSessionIdDisplay();
      this.updateActiveChatSessionMetadataInApi(mapped.id);
    }
    await this.refreshAtlasChatUsageStatus();
    return mapped.id;
  }


  private async sendUserMessageToAtlasChatSession(
    message: ChatMessage,
    userText: string,
    knownMessageIds: Set<string>,
    pendingSessionId: string,
    caseNumberOverride: string | null
  ): Promise<{ chatSessionId: string; botMessage: ChatMessage }> {
    const chatSessionId = await this.ensurePersistedChatSessionForSend(pendingSessionId, caseNumberOverride);
    if (!chatSessionId) {
      throw new Error('No chat session id returned');
    }
    this.pendingResponseSessionId = chatSessionId;

    const persisted = this.throwIfHttpError(await firstValueFrom(this.atlasChatService.addAtlasChatMessage(chatSessionId, {
      ...mapChatMessageToApiRequest(message),
      text: userText
    })));

    if (persisted.author === 'ASSISTANT') {
      return {
        chatSessionId,
        botMessage: mapApiMessageToChatMessage(persisted)
      };
    }

    message.id = persisted.id;
    this.replacePendingChatMessage(chatSessionId, message);
    await this.refreshApiChatSessions();
    const refreshedSession = this.apiChatHistorySessions.find(session => session.id === chatSessionId);
    const assistantMessage = (refreshedSession?.messages || [])
      .filter(item => item.author === 'bot' && (!item.id || !knownMessageIds.has(item.id)))
      .pop();

    return {
      chatSessionId,
      botMessage: assistantMessage || this.createBotMessage('No response received.')
    };
  }

  private updateChatMessageFeedbackInApi(message: ChatMessage): void {
    if (!this.atlasPersistedChatSessionId || !message?.id) {
      return;
    }

    this.atlasChatService.updateAtlasChatMessage(this.atlasPersistedChatSessionId, message.id, {
      reaction: message.reaction === 'like' ? 'LIKE' : message.reaction === 'dislike' ? 'DISLIKE' : null,
      reaction_comment: message.reactionComment || null,
      feedback_enabled: message.feedbackEnabled !== false
    }).subscribe({
      next: () => this.refreshApiChatSessions(),
      error: () => {}
    });
  }

  private async refreshApiChatSessions(): Promise<void> {
    await this.loadAtlasChatSessionsFromApi({ reset: true, limit: this.getLoadedApiChatHistoryLimit() });
    this.applyPendingChatResponsesToApiSessions();
    this.refreshOpenChatHistoryDialogSessions();
  }

  private upsertApiChatHistorySession(session: AtlasChatHistorySession): void {
    if (this.removedChatSessionIds.has(session.id)) {
      return;
    }

    const existingIndex = this.apiChatHistorySessions.findIndex(item => isSameChatHistorySession(item, session));
    if (existingIndex >= 0) {
      this.apiChatHistorySessions[existingIndex] = session;
      return;
    }

    this.apiChatHistorySessions = [...this.apiChatHistorySessions, session];
  }

  private applyChatHistorySessionChange(sessionId: string, changes: Partial<AtlasChatHistorySession>): void {
    if (this.removedChatSessionIds.has(sessionId)) {
      return;
    }

    this.apiChatHistorySessions = this.apiChatHistorySessions.map(session =>
      session.id === sessionId ? { ...session, ...changes } : session
    );

    if (this.atlasPersistedChatSessionId === sessionId) {
      if (changes.sessionDescription !== undefined) {
        this.sessionDescription = changes.sessionDescription || '';
      }
    }

    this.refreshOpenChatHistoryDialogSessions();
  }

  private markChatHistorySessionAsCurrent(sessionId: string): void {
    if (!sessionId || sessionId.startsWith('current-')) {
      return;
    }

    this.atlasChatService.continueAtlasChatSession(sessionId).subscribe({
      next: session => {
        this.upsertApiChatHistorySession(mapApiSessionToHistorySession(session));
        this.refreshOpenChatHistoryDialogSessions();
      },
      error: () => {}
    });
  }

  private markLocalChatHistorySessionAsCurrent(sessionId: string): void {
    if (!sessionId || sessionId.startsWith('current-')) {
      return;
    }

    this.apiChatHistorySessions = this.apiChatHistorySessions.map(session => ({
      ...session,
      isCurrent: session.id === sessionId
    }));
  }

  private async initializeAtlasChatState(): Promise<void> {
    this.isInitializingAtlasChatState = true;
    await this.updateCurrentCaseLabel();
    await this.refreshAtlasChatUsageStatus();
    if (!this.isAtlasBackendAvailable) {
      this.isInitializingAtlasChatState = false;
      return;
    }

    await this.loadAtlasChatSessionsFromApi({ reset: true });
    this.isInitializingAtlasChatState = false;

    if (!this.isOpen) {
      return;
    }

    if (!this.hasSelectedCase) {
      await this.synchronizeActiveChatSessionWithOverview();
      return;
    }

    const currentSession = this.apiChatHistorySessions.find(session => session.isCurrent) || null;
    if (currentSession) {
      this.loadChatHistorySession(currentSession);
      return;
    }

    const created = await this.createNewChatForCase(false, this.currentCaseNumber);
    if (!created && this.hasAtlasChatSessionCreationLimitReached) {
      this.blockChatForOverviewSessionCreationLimit();
    }
  }

  private async refreshAtlasChatUsageStatus(): Promise<void> {
    try {
      this.chatUsageStatus = this.throwIfHttpError(await firstValueFrom(this.atlasChatService.getAtlasChatUsageStatus()));
      this.isAtlasBackendAvailable = true;
    } catch {
      this.chatUsageStatus = null;
      this.isAtlasBackendAvailable = false;
    }

    this.refreshOpenChatHistoryDialogSessions();
  }

  private async loadAtlasChatSessionsFromApi(options: { reset?: boolean; limit?: number; offset?: number } = {}): Promise<void> {
    const reset = options.reset !== false;
    const offset = options.offset ?? (reset ? 0 : this.apiChatHistorySessions.length);
    const limit = options.limit ?? this.chatHistoryPageSize;

    try {
      const response = await firstValueFrom(this.atlasChatService.listAtlasChatSessions({
        includeMessages: true,
        limit,
        offset
      }));
      this.isAtlasBackendAvailable = true;
      const sessions = (response?.sessions || [])
        .map(session => mapApiSessionToHistorySession(session))
        .filter(session => !this.removedChatSessionIds.has(session.id));
      this.apiChatHistoryTotal = response?.total || sessions.length;
      this.apiChatHistorySessions = reset ? sessions : this.mergeApiChatHistorySessions(this.apiChatHistorySessions, sessions);
      this.applyPendingChatResponsesToApiSessions();
    } catch {
      this.isAtlasBackendAvailable = false;
      if (reset) {
        this.apiChatHistorySessions = [];
        this.apiChatHistoryTotal = 0;
      }
    }
  }

  private async loadMoreChatHistorySessions(): Promise<void> {
    if (this.isLoadingMoreChatHistorySessions || !this.hasMoreApiChatHistorySessions()) {
      return;
    }

    this.isLoadingMoreChatHistorySessions = true;
    this.refreshOpenChatHistoryDialogSessions();

    try {
      await this.loadAtlasChatSessionsFromApi({
        reset: false,
        limit: this.chatHistoryPageSize,
        offset: this.apiChatHistorySessions.length
      });
    } finally {
      this.isLoadingMoreChatHistorySessions = false;
      this.refreshOpenChatHistoryDialogSessions();
    }
  }

  private mergeApiChatHistorySessions(
    existingSessions: AtlasChatHistorySession[],
    nextSessions: AtlasChatHistorySession[]
  ): AtlasChatHistorySession[] {
    const merged = [...existingSessions];
    nextSessions.forEach(session => {
      const existingIndex = merged.findIndex(item => isSameChatHistorySession(item, session));
      if (existingIndex >= 0) {
        merged[existingIndex] = session;
        return;
      }

      merged.push(session);
    });

    return merged;
  }

  private getLoadedApiChatHistoryLimit(): number {
    return Math.max(this.chatHistoryPageSize, this.apiChatHistorySessions.length);
  }

  private hasMoreApiChatHistorySessions(): boolean {
    return this.apiChatHistorySessions.length < this.apiChatHistoryTotal;
  }

  private clearAtlasChatSession(): void {
    this.atlasChatSessionId = null;
    this.updateSessionIdDisplay();
  }

  private isActiveChatSession(sessionId: string | null | undefined): boolean {
    if (sessionId === this.localPendingResponseSessionId) {
      const pending = this.pendingChatResponses.get(this.localPendingResponseSessionId);
      return !this.atlasPersistedChatSessionId && this.isPendingChatForCurrentCase(pending?.caseNumber || null);
    }

    return !!sessionId && sessionId === this.atlasPersistedChatSessionId;
  }

  private getActivePendingChatResponse(): { messages: ChatMessage[] } | null {
    if (this.atlasPersistedChatSessionId) {
      return this.pendingChatResponses.get(this.atlasPersistedChatSessionId) || null;
    }

    const pending = this.pendingChatResponses.get(this.localPendingResponseSessionId) || null;
    return pending && this.isPendingChatForCurrentCase(pending.caseNumber) ? pending : null;
  }

  private isPendingChatForCurrentCase(caseNumber: string | null): boolean {
    const normalizedPendingCaseNumber = normalizeCaseNumberForLookup(caseNumber);
    const normalizedCurrentCaseNumber = this.hasSelectedCase ? normalizeCaseNumberForLookup(this.currentCaseNumber) : null;

    if (!normalizedPendingCaseNumber) {
      return !normalizedCurrentCaseNumber;
    }

    return normalizedPendingCaseNumber === normalizedCurrentCaseNumber;
  }

  private upsertPendingChatResponse(
    sessionId: string,
    changes: Partial<{
      messages: ChatMessage[];
      caseNumber: string | null;
      sessionDescription: string;
      caseContextModeEnabled: boolean;
    }>
  ): void {
    const existing = this.pendingChatResponses.get(sessionId);
    this.pendingChatResponses.set(sessionId, {
      sessionId,
      messages: normalizeChatMessages(JSON.parse(JSON.stringify(changes.messages || existing?.messages || []))),
      caseNumber: changes.caseNumber !== undefined ? changes.caseNumber : existing?.caseNumber || null,
      sessionDescription: changes.sessionDescription !== undefined ? changes.sessionDescription : existing?.sessionDescription || '',
      caseContextModeEnabled: changes.caseContextModeEnabled !== undefined
        ? changes.caseContextModeEnabled
        : existing?.caseContextModeEnabled || false,
      createdAt: existing?.createdAt || new Date().toISOString()
    });
    this.applyPendingChatResponsesToApiSessions();
  }

  private movePendingChatResponse(
    fromSessionId: string,
    toSessionId: string,
    changes: Partial<{ caseNumber: string | null; sessionDescription: string; caseContextModeEnabled: boolean }> = {}
  ): void {
    const existing = this.pendingChatResponses.get(fromSessionId);
    if (!existing || fromSessionId === toSessionId) {
      return;
    }

    this.pendingChatResponses.delete(fromSessionId);
    this.pendingChatResponses.set(toSessionId, {
      ...existing,
      ...changes,
      sessionId: toSessionId
    });
    if (this.pendingResponseSessionId === fromSessionId) {
      this.pendingResponseSessionId = toSessionId;
    }
    this.applyPendingChatResponsesToApiSessions();
  }

  private replacePendingChatMessage(sessionId: string, message: ChatMessage): void {
    const pending = this.pendingChatResponses.get(sessionId);
    if (!pending) {
      return;
    }

    const messages = pending.messages.map(item => item.author === 'user'
      && item.timestamp === message.timestamp
      && item.text === message.text
      ? { ...message }
      : item);
    this.upsertPendingChatResponse(sessionId, { messages });
  }

  private getPendingChatMessages(sessionId: string | null | undefined): ChatMessage[] {
    if (!sessionId) {
      return normalizeChatMessages(JSON.parse(JSON.stringify(this.messages || [])));
    }

    const pending = this.pendingChatResponses.get(sessionId);
    return normalizeChatMessages(JSON.parse(JSON.stringify(pending?.messages || this.messages || [])));
  }

  private completePendingChatResponse(sessionId: string, messages: ChatMessage[]): void {
    const pending = this.pendingChatResponses.get(sessionId);
    this.pendingChatResponses.delete(sessionId);
    const normalizedMessages = getHistoryDialogMessages(normalizeChatMessages(JSON.parse(JSON.stringify(messages || []))));

    if (pending && sessionId !== this.localPendingResponseSessionId) {
      this.upsertApiChatHistorySession({
        id: sessionId,
        sessionId,
        createdAt: pending.createdAt,
        sessionDescription: pending.sessionDescription,
        overallScore: this.apiChatHistorySessions.find(session => session.id === sessionId)?.overallScore ?? 0,
        caseLabel: pending.caseNumber || '-',
        caseNumber: pending.caseNumber || '-',
        caseContextModeEnabled: pending.caseContextModeEnabled,
        messages: normalizedMessages,
        isCurrent: this.atlasPersistedChatSessionId === sessionId
      });
    }

    if (this.pendingResponseSessionId === sessionId) {
      this.pendingResponseSessionId = null;
    }
  }

  private applyPendingChatResponsesToApiSessions(): void {
    if (!this.pendingChatResponses.size) {
      return;
    }

    this.pendingChatResponses.forEach(pending => {
      if (pending.sessionId === this.localPendingResponseSessionId) {
        return;
      }

      const existing = this.apiChatHistorySessions.find(session => session.id === pending.sessionId);
      this.upsertApiChatHistorySession({
        id: pending.sessionId,
        sessionId: pending.sessionId,
        createdAt: existing?.createdAt || pending.createdAt,
        sessionDescription: pending.sessionDescription || existing?.sessionDescription || '',
        overallScore: existing?.overallScore ?? 0,
        caseLabel: pending.caseNumber || existing?.caseLabel || '-',
        caseNumber: pending.caseNumber || existing?.caseNumber || '-',
        caseContextModeEnabled: pending.caseContextModeEnabled || existing?.caseContextModeEnabled || false,
        messages: getHistoryDialogMessages(normalizeChatMessages(JSON.parse(JSON.stringify(pending.messages || [])))),
        isCurrent: this.atlasPersistedChatSessionId === pending.sessionId
      });
    });
  }

  private createAtlasChatSessionRequest(caseNumberOverride?: string | null): { case_number?: string | null } {
    const caseNumber = caseNumberOverride !== undefined ? caseNumberOverride : this.getStoredActiveSessionCaseNumber();
    return caseNumber ? { case_number: caseNumber } : {};
  }

  private async applyCaseContextDataToMessage(message: ChatMessage): Promise<void> {
    message.caseContextData = await this.buildAtlasCaseContextData();
  }

  private async buildAtlasCaseContextData(): Promise<AtlasChatCaseContextData | null> {
    if (!this.isCaseContextModeOn) {
      return null;
    }

    return this.atlasCaseContextService.buildAtlasCaseContextData(this.getActiveCaseId(), this.contextFieldSettings);
  }

  private normalizeNationalId(value: string | null | undefined): string {
    return (value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  }

  private containsNationalIdPattern(value: string): boolean {
    if (!value) {
      return false;
    }

    const dashedPattern = /\b\d{6}-\d{4}\b/;
    const plainTenDigitsPattern = /(^|[^\d])\d{10,11}($|[^\d])/;
    const emailPattern = /\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/i;
    const phonePattern = /(?:^|[^\w])(?:\+?\d[\d\s\-()]{7,}\d)(?:$|[^\w])/;

    return dashedPattern.test(value) || plainTenDigitsPattern.test(value) || emailPattern.test(value) || phonePattern.test(value);
  }

  private archiveCurrentChatSession(
    caseNumberOverride?: string | null,
    refreshOnSuccess = true
  ): void {
    if (!this.hasConversationContent()) {
      return;
    }

    if (!this.atlasPersistedChatSessionId) {
      return;
    }

    const hasSentCaseContextMessage = this.hasActiveCaseContextMode();
    this.atlasChatService.updateAtlasChatSession(this.atlasPersistedChatSessionId, {
      status: 'ARCHIVED',
      case_number: hasSentCaseContextMessage ? (caseNumberOverride || this.getStoredActiveSessionCaseNumber()) : null,
      session_description: (this.sessionDescription || '').trim(),
      case_context_mode_enabled: hasSentCaseContextMessage
    }).subscribe({
      next: () => {
        if (refreshOnSuccess) {
          this.refreshApiChatSessions();
        }
      },
      error: () => {}
    });
  }

  private getStoredChatHistorySessions(): AtlasChatHistorySession[] {
    return this.apiChatHistorySessions.map(session => normalizeChatHistorySession(session));
  }

  private refreshOpenChatHistoryDialogSessions(): void {
    if (!this.chatHistoryDialogRef?.componentInstance) {
      return;
    }

    const sessions = this.getChatHistoryDialogSessions();
    this.chatHistoryDialogRef.componentInstance.data.sessions = [...sessions];
    this.chatHistoryDialogRef.componentInstance.data.hasMoreSessions = this.hasMoreApiChatHistorySessions();
    this.chatHistoryDialogRef.componentInstance.data.isLoadingMoreSessions = this.isLoadingMoreChatHistorySessions;
  }

  private async continueChatHistorySession(session: AtlasChatHistorySession): Promise<void> {
    if (!session) {
      return;
    }

    this.clearCaseChatSessionCreationLimitBlock();
    const activeSessionCaseNumber = this.getStoredActiveSessionCaseNumber() || this.legacyActiveSessionCaseNumber || this.currentCaseNumber;
    const isSameActiveSession = session.id && session.id === this.atlasPersistedChatSessionId;

    if (!isSameActiveSession && this.hasConversationContent()) {
      this.archiveCurrentChatSession(activeSessionCaseNumber, false);
    }

    this.markLocalChatHistorySessionAsCurrent(session.id);
    this.refreshOpenChatHistoryDialogSessions();
    await this.navigateToChatHistorySessionCase(session);
    this.loadChatHistorySession(session);
    this.markChatHistorySessionAsCurrent(session.id);
    this.refreshOpenChatHistoryDialogSessions();
  }

  private async synchronizeActiveChatSessionWithCase(): Promise<void> {
    const nextCaseNumber = this.hasSelectedCase && this.currentCaseNumber !== '-' ? this.currentCaseNumber : null;
    const storedCaseNumber = this.getStoredActiveSessionCaseNumber() || this.legacyActiveSessionCaseNumber;

    if (!nextCaseNumber) {
      this.activeSessionCaseNumber = null;
      this.clearCaseChatSessionCreationLimitBlock();
      return;
    }

    if (!this.isOpen) {
      return;
    }

    if (!storedCaseNumber) {
      const nextCaseSession = await this.findLatestAvailableChatSessionForCase(nextCaseNumber);
      if (nextCaseSession) {
        this.activateChatHistorySession(nextCaseSession);
        this.legacyActiveSessionCaseNumber = null;
        this.clearCaseChatSessionCreationLimitBlock();
        return;
      }

      if (this.hasReusableActiveUnassignedChat()) {
        this.legacyActiveSessionCaseNumber = null;
        this.clearCaseChatSessionCreationLimitBlock();
        return;
      }

      if (this.restoreLatestUnassignedChatSession()) {
        this.legacyActiveSessionCaseNumber = null;
        this.clearCaseChatSessionCreationLimitBlock();
        return;
      }

      const created = await this.createNewChatForCase(false, nextCaseNumber);
      if (!created) {
        this.prepareEmptyCaseChatView();
      }
      this.legacyActiveSessionCaseNumber = null;
      this.clearCaseChatSessionCreationLimitBlock();
      return;
    }

    if (storedCaseNumber === nextCaseNumber) {
      this.persistActiveSessionCaseNumber();
      this.legacyActiveSessionCaseNumber = null;
      this.clearCaseChatSessionCreationLimitBlock();
      return;
    }

    const nextCaseSession = await this.findLatestAvailableChatSessionForCase(nextCaseNumber);
    if (nextCaseSession) {
      if (this.hasConversationContent()) {
        this.archiveCurrentChatSession(storedCaseNumber);
        this.refreshOpenChatHistoryDialogSessions();
      }

      this.activateChatHistorySession(nextCaseSession);
      this.legacyActiveSessionCaseNumber = null;
      this.clearCaseChatSessionCreationLimitBlock();
      return;
    }

    if (this.restoreLatestUnassignedChatSession()) {
      this.legacyActiveSessionCaseNumber = null;
      this.clearCaseChatSessionCreationLimitBlock();
      return;
    }

    if (this.hasConversationContent()) {
      this.archiveCurrentChatSession(storedCaseNumber);
      this.refreshOpenChatHistoryDialogSessions();
    }

    const created = await this.createNewChatForCase(false, nextCaseNumber);
    if (!created) {
      this.prepareEmptyCaseChatView();
    }
    this.legacyActiveSessionCaseNumber = null;
    this.clearCaseChatSessionCreationLimitBlock();
  }

  private async findLatestAvailableChatSessionForCase(caseNumber: string): Promise<AtlasChatHistorySession | null> {
    const localSession = this.findLatestChatSessionForCase(caseNumber);
    if (localSession) {
      return localSession;
    }

    const session = await this.loadLatestChatSessionForCaseFromApi(caseNumber);
    if (!session) {
      return null;
    }

    this.upsertApiChatHistorySession(session);
    return session;
  }

  private async loadLatestChatSessionForCaseFromApi(caseNumber: string): Promise<AtlasChatHistorySession | null> {
    try {
      const response = await firstValueFrom(this.atlasChatService.listAtlasChatSessions({
        caseNumber,
        includeMessages: true,
        limit: 1,
        offset: 0
      }));
      const session = (response?.sessions || [])[0];
      this.isAtlasBackendAvailable = true;
      return session ? mapApiSessionToHistorySession(session) : null;
    } catch {
      this.isAtlasBackendAvailable = false;
      return null;
    }
  }

  private async synchronizeActiveChatSessionWithOverview(): Promise<void> {
    if (!this.isOpen) {
      return;
    }

    if (this.hasReusableActiveUnassignedChat()) {
      this.activeSessionCaseNumber = null;
      this.clearChatSessionCreationLimitBlock();
      this.updateActiveChatSessionMetadataInApi();
      this.refreshOpenChatHistoryDialogSessions();
      return;
    }

    await this.loadAtlasChatSessionsFromApi({ reset: true });
    const latestUnassignedSession = this.findLatestUnassignedChatSession();
    if (latestUnassignedSession) {
      this.loadChatHistorySession(latestUnassignedSession);
      this.markLocalChatHistorySessionAsCurrent(latestUnassignedSession.id);
      this.markChatHistorySessionAsCurrent(latestUnassignedSession.id);
      this.refreshOpenChatHistoryDialogSessions();
      this.clearChatSessionCreationLimitBlock();
      return;
    }

    if (!this.activeChatHasCaseContext()) {
      this.activeSessionCaseNumber = null;

      if (this.atlasPersistedChatSessionId && this.hasConversationContent()) {
        this.clearChatSessionCreationLimitBlock();
        this.updateActiveChatSessionMetadataInApi();
        this.refreshOpenChatHistoryDialogSessions();
        return;
      }
    }

    const created = await this.createNewChatForCase(false, null);
    if (!created) {
      this.clearActiveChatView();
      this.messages = this.createInitialMessages();
      if (this.hasAtlasChatSessionCreationLimitReached) {
        this.blockChatForOverviewSessionCreationLimit();
        return;
      }
    }
    this.clearChatSessionCreationLimitBlock();
    this.refreshOpenChatHistoryDialogSessions();
  }

  private activateChatHistorySession(session: AtlasChatHistorySession): void {
    this.loadChatHistorySession(session);
    this.markLocalChatHistorySessionAsCurrent(session.id);
    this.markChatHistorySessionAsCurrent(session.id);
    this.refreshOpenChatHistoryDialogSessions();
    this.clearCaseChatSessionCreationLimitBlock();
  }

  private findLatestChatSessionForCase(caseNumber: string): AtlasChatHistorySession | null {
    return this.getStoredChatHistorySessions()
      .filter(item => item.caseNumber === caseNumber)
      .sort((first, second) => getChatHistorySessionSortTime(second) - getChatHistorySessionSortTime(first))[0] || null;
  }

  private restoreLatestUnassignedChatSession(): boolean {
    const session = this.findLatestUnassignedChatSession();
    if (!session) {
      return false;
    }

    this.loadChatHistorySession(session);
    this.markLocalChatHistorySessionAsCurrent(session.id);
    this.markChatHistorySessionAsCurrent(session.id);
    this.refreshOpenChatHistoryDialogSessions();
    this.clearCaseChatSessionCreationLimitBlock();
    return true;
  }

  private prepareEmptyCaseChatView(): void {
    this.atlasPersistedChatSessionId = null;
    this.clearAtlasChatSession();
    this.activeSessionCaseNumber = null;
    this.activeSessionCaseContextModeEnabled = false;
    this.sessionDescription = '';
    this.messages = this.createInitialMessages();
    this.shouldScrollToBottom = true;
    this.persistMessages();
    this.refreshOpenChatHistoryDialogSessions();
  }

  private findLatestUnassignedChatSession(): AtlasChatHistorySession | null {
    return this.getStoredChatHistorySessions()
      .filter(session => !normalizeCaseNumberForLookup(session.caseNumber))
      .sort((first, second) => getChatHistorySessionSortTime(second) - getChatHistorySessionSortTime(first))[0] || null;
  }

  private async createNewChatForCase(
    archiveCurrentOnSuccess = false,
    caseNumberOverride?: string | null
  ): Promise<boolean> {
    if (!this.isAtlasBackendAvailable || this.isCreatingNewChat || !this.canCreateAtlasChatSession) {
      return false;
    }

    this.isCreatingNewChat = true;
    this.shouldScrollToBottom = true;

    try {
      const created = this.throwIfHttpError(await firstValueFrom(this.atlasChatService.createAtlasChatSession(
        this.createAtlasChatSessionRequest(caseNumberOverride)
      )));
      if (archiveCurrentOnSuccess) {
        this.archiveCurrentChatSession();
      }

      const mapped = mapApiSessionToHistorySession(created);
      this.atlasPersistedChatSessionId = mapped.id;
      this.atlasChatSessionId = mapped.id;
      this.activeSessionCaseNumber = normalizeCaseNumberForLookup(mapped.caseNumber)
        ? mapped.caseNumber
        : (caseNumberOverride || null);
      this.activeSessionCaseContextModeEnabled = !!mapped.caseContextModeEnabled;
      this.sessionDescription = '';
      this.messages = mapped.messages.length ? mapped.messages : this.createInitialMessages();
      this.updateSessionIdDisplay();
      this.upsertApiChatHistorySession(mapped);
      if (caseNumberOverride === undefined) {
        this.updateActiveChatSessionMetadataInApi();
      }
      this.finishCreatingNewChat();
      void this.refreshAtlasChatUsageStatus();
      this.clearCaseChatSessionCreationLimitBlock();
      return true;
    } catch (error) {
      this.showAtlasChatSessionCreationError(error);
      await this.refreshAtlasChatUsageStatus();
      return false;
    } finally {
      if (this.isCreatingNewChat) {
        this.finishCreatingNewChat();
      }
    }
  }

  private finishCreatingNewChat(): void {
    this.isCreatingNewChat = false;
    this.shouldScrollToBottom = true;
    this.persistMessages();
    this.refreshOpenChatHistoryDialogSessions();
  }

  private loadChatHistorySession(session: AtlasChatHistorySession): void {
    const normalizedSession = normalizeChatHistorySession(session);
    this.atlasPersistedChatSessionId = normalizedSession.id?.startsWith('current-') ? this.atlasPersistedChatSessionId : normalizedSession.id;
    const pending = this.pendingChatResponses.get(this.atlasPersistedChatSessionId);
    this.messages = normalizeChatMessages(JSON.parse(JSON.stringify(pending?.messages || normalizedSession.messages || [])));
    this.sessionDescription = normalizedSession.sessionDescription || '';
    this.shouldScrollToBottom = true;
    this.persistMessages();
    this.activeSessionCaseNumber = normalizeCaseNumberForLookup(normalizedSession.caseNumber)
      ? normalizedSession.caseNumber
      : null;
    this.activeSessionCaseContextModeEnabled = !!normalizedSession.caseContextModeEnabled;
    if (this.hasSelectedCase && this.activeSessionCaseContextModeEnabled) {
      this.useCurrentCaseContext = true;
    }

    if (normalizedSession.sessionId && normalizedSession.sessionId !== '-') {
      this.atlasChatSessionId = normalizedSession.id || normalizedSession.sessionId;
    } else {
      this.clearAtlasChatSession();
    }

    this.updateSessionIdDisplay();
    this.isOpen = true;
    this.hasUnreadBotResponse = false;
  }

  private removeStoredChatHistorySession(sessionId: string): void {
    if (!sessionId) {
      return;
    }

    this.handleChatHistorySessionRemoved(sessionId);
    this.atlasChatService.deleteAtlasChatSession(sessionId).subscribe({
      next: () => this.refreshApiChatSessions(),
      error: () => {}
    });
  }

  private handleChatHistorySessionRemoved(sessionId: string): void {
    if (!sessionId) {
      return;
    }

    this.removedChatSessionIds.add(sessionId);
    this.chatSessionMessageLimitReachedSessionIds.delete(sessionId);
    this.apiChatHistorySessions = this.apiChatHistorySessions.filter(session => session.id !== sessionId);

    if (this.atlasPersistedChatSessionId === sessionId) {
      this.atlasPersistedChatSessionId = null;
      this.clearAtlasChatSession();
      this.activeSessionCaseContextModeEnabled = false;
      this.messages = this.createInitialMessages();
      this.sessionDescription = '';
      this.persistActiveSessionCaseNumber();
    }

    this.refreshOpenChatHistoryDialogSessions();
  }

  private getChatHistoryDialogSessions(): AtlasChatHistorySession[] {
    const currentSession = this.createCurrentChatHistorySessionSnapshot();
    const storedSessions = this.getStoredChatHistorySessions();

    if (!currentSession) {
      return storedSessions;
    }

    const storedWithoutCurrentDuplicate = storedSessions.filter(session => !isSameChatHistorySession(session, currentSession));

    return [...storedWithoutCurrentDuplicate, currentSession];
  }

  private createCurrentChatHistorySessionSnapshot(): AtlasChatHistorySession | null {
    if (!this.hasVisibleCurrentChat()) {
      return null;
    }

    const caseNumber = this.getStoredActiveSessionCaseNumber() || this.legacyActiveSessionCaseNumber || '-';
    const cachedCurrentSession = this.atlasPersistedChatSessionId
      ? this.apiChatHistorySessions.find(session => session.id === this.atlasPersistedChatSessionId)
      : null;

    return {
      id: this.atlasPersistedChatSessionId || `current-${this.atlasChatSessionId || 'local'}`,
      sessionId: this.atlasChatSessionId || this.sessionIdDisplay || '-',
      createdAt: cachedCurrentSession?.createdAt || new Date().toISOString(),
      sessionDescription: (this.sessionDescription || '').trim(),
      overallScore: cachedCurrentSession?.overallScore ?? 0,
      caseLabel: this.activeChatCaseLabel,
      caseNumber,
      caseContextModeEnabled: this.hasActiveCaseContextMode(),
      messages: getHistoryDialogMessages(normalizeChatMessages(JSON.parse(JSON.stringify(this.messages)))),
      isCurrent: true
    };
  }

  private async navigateToChatHistorySessionCase(session: AtlasChatHistorySession): Promise<void> {
    const caseNumber = normalizeCaseNumberForLookup(session?.caseNumber);
    if (!caseNumber || caseNumber === '-' || caseNumber === this.currentCaseNumber) {
      return;
    }

    const caseId = await this.atlasCaseNumberLookupService.resolveCaseIdByCaseNumber(caseNumber);
    if (!caseId) {
      return;
    }

    if (caseId === this.getActiveCaseId()) {
      this.dataService.send({ caseId: +caseId, newlyCreated: false });
      return;
    }

    const navigated = await this.router.navigateByUrl(`/case/${caseId}`);
    if (!navigated) {
      window.location.hash = `/case/${caseId}`;
    }

    this.dataService.send({ caseId: +caseId, newlyCreated: false });
  }

  private persistActiveSessionCaseNumber(): void {
    if (!this.hasActiveCaseContextMode()) {
      this.activeSessionCaseNumber = null;
    }
    this.updateActiveChatSessionMetadataInApi();
  }

  private assignCaseNumberAfterCaseContextMessage(message: ChatMessage, sessionId = this.atlasPersistedChatSessionId): void {
    if (!message?.caseContextModeEnabled || !message.caseContextCaseNumberValue) {
      return;
    }

    if (this.isActiveChatSession(sessionId)) {
      this.activeSessionCaseNumber = message.caseContextCaseNumberValue;
    }
    this.updateActiveChatSessionMetadataInApi(sessionId, message.caseContextCaseNumberValue, true);
  }

  private updateActiveChatSessionMetadataInApi(
    sessionId = this.atlasPersistedChatSessionId,
    caseNumber = this.activeSessionCaseNumber,
    caseContextModeEnabled = this.hasActiveCaseContextMode()
  ): void {
    if (!sessionId) {
      return;
    }

    this.atlasChatService.updateAtlasChatSession(sessionId, {
      case_number: caseContextModeEnabled ? caseNumber : null,
      session_description: this.sessionDescription || null,
      case_context_mode_enabled: caseContextModeEnabled
    }).subscribe({
      next: () => this.refreshApiChatSessions(),
      error: () => {}
    });
  }

  private getStoredActiveSessionCaseNumber(): string | null {
    return this.activeSessionCaseNumber;
  }

  private hasConversationContent(): boolean {
    return (this.messages || []).some(message => message.author === 'user' || message.feedbackEnabled !== false);
  }

  private hasSentUserMessage(): boolean {
    return (this.messages || []).some(message => message.author === 'user');
  }

  private hasVisibleCurrentChat(): boolean {
    return (this.messages || []).length > 0;
  }

  private hasReusableActiveUnassignedChat(): boolean {
    return !!this.atlasPersistedChatSessionId
      && this.hasVisibleCurrentChat()
      && !this.getStoredActiveSessionCaseNumber()
      && !this.activeChatHasCaseContext();
  }

  private activeChatHasCaseContext(): boolean {
    return !!this.getStoredActiveSessionCaseNumber()
      || (this.messages || []).some(message => !!message.caseContextModeEnabled);
  }

  private hasSentCaseContextMessage(): boolean {
    return (this.messages || []).some(message => message.author === 'user' && !!message.caseContextModeEnabled);
  }

  private hasActiveCaseContextMode(): boolean {
    return this.activeSessionCaseContextModeEnabled || this.hasSentCaseContextMessage();
  }

  private clearActiveChatView(): void {
    this.atlasPersistedChatSessionId = null;
    this.clearAtlasChatSession();
    this.activeSessionCaseNumber = null;
    this.activeSessionCaseContextModeEnabled = false;
    this.legacyActiveSessionCaseNumber = null;
    this.sessionDescription = '';
    this.messages = [];
    this.shouldScrollToBottom = true;
    this.refreshOpenChatHistoryDialogSessions();
  }

  private blockChatForOverviewSessionCreationLimit(): void {
    if (!this.hasAtlasChatSessionCreationLimitReached) {
      return;
    }

    this.blockedGeneralChatSessionCreationLimit = true;
    this.clearActiveChatView();
  }

  private clearCaseChatSessionCreationLimitBlock(): void {
    this.clearChatSessionCreationLimitBlock();
  }

  private clearChatSessionCreationLimitBlock(): void {
    this.blockedCaseChatSessionCreationLimitCaseNumber = null;
    this.blockedGeneralChatSessionCreationLimit = false;
  }

  private createMessageTimestamp(): string {
    return new Date().toISOString();
  }

  private getPreferredUsername(): string {
    const keycloak = this.keycloakService.getKeycloakInstance();
    const parsedToken = keycloak?.tokenParsed as { preferred_username?: string; upn?: string; email?: string } | undefined;
    return (parsedToken?.preferred_username || this.keycloakService.getUsername() || parsedToken?.upn || parsedToken?.email || '-').trim();
  }


  private updateSessionIdDisplay(): void {
    if (!this.atlasChatSessionId) {
      this.sessionIdDisplay = '-';
      return;
    }

    this.sessionIdDisplay = this.atlasChatSessionId;
  }

  private async updateAtlasTemplateContext(activeCase: Case): Promise<void> {
    if (!activeCase?.incident?.id) {
      this.atlasTemplateContext = null;
      return;
    }

    try {
      const incident = await firstValueFrom(this.incidentService.getCaseIncident(activeCase.incident.id));
      this.atlasTemplateContext = {
        caseNumber: activeCase?.caseNumber ? String(activeCase.caseNumber) : null,
        caseId: activeCase?.id != null ? String(activeCase.id) : null,
        incidentCauseLevel1Code: incident?.causeLevel1Code || null,
        incidentCauseLevel2Code: incident?.causeLevel2Code || null,
        incidentCauseLevel3Code: incident?.causeLevel3Code || null,
        customerProfileId: await this.resolveCustomerProfileId(activeCase.id),
        isWintherSportEnabled: incident?.winterSport ?? null
      };
    } catch {
      this.atlasTemplateContext = null;
    }
  }

  private updateAtlasTemplateContextFromIncidentState(incidentState: CaseIncidentIdWithCaseId | null | undefined): void {
    if (!incidentState?.incident || String(incidentState.caseId) !== String(this.getActiveCaseId())) {
      return;
    }

    if (!this.atlasTemplateContext) {
      return;
    }

    this.atlasTemplateContext = {
      ...this.atlasTemplateContext,
      incidentCauseLevel1Code: incidentState.incident.causeLevel1Code || null,
      incidentCauseLevel2Code: incidentState.incident.causeLevel2Code || null,
      incidentCauseLevel3Code: incidentState.incident.causeLevel3Code || null,
      isWintherSportEnabled: incidentState.incident.winterSport ?? this.atlasTemplateContext.isWintherSportEnabled
    };
  }

  private async resolveCustomerProfileId(caseId: string): Promise<string | null> {
    try {
      const personInsurance = await this.atlasCaseContextService.getCurrentPersonInsurance(caseId);
      return personInsurance?.customerProfileId != null ? String(personInsurance.customerProfileId) : null;
    } catch {
      return null;
    }
  }
}
